// Referrals in Account, at /account#referrals (decision 55, 13 Oct; docs/referrals-spec.md section 3). No
// board draws it: it's the old site's Referrals page in the shell's look (Manrope, white, the near-black ink,
// greys as tints of it, and gold only as a small dot that carries no text).
//
// 2 states, from the member's own 2 reads (account/referrals.js):
// - Quiet: nothing ticked and nothing to show. The heading and 1 line, and nothing else: no product, no fee,
//   no locked tile and no button (decision 23: nothing pitches a product they don't refer).
// - Full: the lede, the 3 totals, 1 card per product Adam has ticked for them (their link with Copy and
//   WhatsApp, the fee line and the details to read before introducing someone), then their referrals, newest
//   first, by name only. With lines but nothing ticked, the totals and the list still show, with no cards, under
//   a lede that offers no link.
// The section loads on its own, so a read that fails says so here and never fails Account. While staff
// view as a member, it's that member's section, and Copy and WhatsApp are disabled (Account's read-only rule).
// It never shows an email, a code on its own, a source or a flag: the reads don't give them.
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { supabase, must } from '../lib/supabase.js'
import { copyText } from '../lib/clipboard.js'
import { readOnlyControl } from '../lib/viewAs.jsx'
import { COPY } from './copy.js'
import { buildRefLink, payLine, gbp, totals, dayMonthYear, isQuiet, whatsAppHref } from './referrals.js'

const R = COPY.referrals

// The member's 2 reads at once (account/referrals.js says what each gives). Either failing fails the section (its
// own error state), never Account.
async function loadReferrals() {
  const [offers, lines] = await Promise.all([
    supabase.rpc('my_referral_offers').then(must),
    supabase.rpc('my_introductions').then(must),
  ])
  return { offers: offers || [], lines: lines || [] }
}
const COPIED_MS = 2200

function Details({ offer, id }) {
  const points = (offer.key_points || []).filter(Boolean)
  const never = (offer.never_say || []).filter(Boolean)
  return (
    <div className="ac-ref-details" id={id}>
      {offer.positioning_line && <p className="ac-ref-positioning">{offer.positioning_line}</p>}
      {offer.who_its_for && (
        <div className="ac-ref-part">
          <h4 className="ac-ref-eyebrow">{R.whoFor}</h4>
          <p className="ac-ref-text">{offer.who_its_for}</p>
        </div>
      )}
      {points.length > 0 && (
        <div className="ac-ref-part">
          <h4 className="ac-ref-eyebrow">{R.keyPoints}</h4>
          <ul className="ac-ref-bullets">{points.map((k, i) => <li key={i}>{k}</li>)}</ul>
        </div>
      )}
      {offer.price_terms && (
        <div className="ac-ref-part">
          <h4 className="ac-ref-eyebrow">{R.priceTerms}</h4>
          <p className="ac-ref-text">{offer.price_terms}</p>
        </div>
      )}
      {offer.how_to_introduce && (
        <div className="ac-ref-part">
          <h4 className="ac-ref-eyebrow">{R.howTo}</h4>
          <p className="ac-ref-text">{offer.how_to_introduce}</p>
        </div>
      )}
      {never.length > 0 && (
        <div className="ac-ref-part">
          <h4 className="ac-ref-eyebrow">{R.neverSay}</h4>
          <ul className="ac-ref-bullets">{never.map((k, i) => <li key={i}>{k}</li>)}</ul>
        </div>
      )}
      <p className="ac-ref-close">{R.close}</p>
    </div>
  )
}

function OfferCard({ offer, readOnly }) {
  const uid = useId()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [line, setLine] = useState({ text: '', error: false })
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])
  const link = buildRefLink(offer, offer.code)
  const fee = payLine(offer)
  // the link's end in view, where their code is, when the field is narrower than the link (a phone; the review of
  // 18 Oct showed only "https://buy.stripe.com/..." there)
  // (again once Manrope has loaded, which widens the text, and whenever the field's width changes)
  const field = useRef(null)
  useEffect(() => {
    const el = field.current
    if (!el) return undefined
    let alive = true
    const end = () => { if (alive) el.scrollLeft = el.scrollWidth }
    end()
    document.fonts?.ready?.then(end, () => {})
    const watch = typeof ResizeObserver === 'function' ? new ResizeObserver(end) : null
    watch?.observe(el)
    return () => { alive = false; watch?.disconnect() }
  }, [link])

  async function copy() {
    if (readOnly) return
    const ok = await copyText(link)
    clearTimeout(timer.current)
    if (ok) {
      setCopied(true)
      setLine({ text: R.copiedLine, error: false })
      timer.current = setTimeout(() => setCopied(false), COPIED_MS)
    } else {
      setCopied(false)
      setLine({ text: R.copyFailed(link), error: true })
    }
  }

  return (
    <article className="ac-ref-offer" aria-labelledby={`${uid}-h`}>
      <div className="ac-ref-offer-head">
        <span className="ac-ref-eyebrow">{R.yourLink}</span>
        <h3 className="ac-ref-h3" id={`${uid}-h`}>{offer.product_name}</h3>
      </div>
      {link ? (
        <>
          <div className="ac-ref-linkrow">
            <input ref={field} className="ac-input ac-ref-input" type="text" readOnly value={link} aria-label={R.linkLabel(offer.product_name)}
              onFocus={e => e.target.select()} onClick={e => e.target.select()} />
            <div className="ac-ref-btns">
              <button type="button" className="ac-btn ac-ref-btn" onClick={copy} {...readOnlyControl(readOnly)}>{copied ? R.copied : R.copy}</button>
              {readOnly
                ? <button type="button" className="ac-btn ac-ref-btn" {...readOnlyControl(true)}>{R.whatsapp}</button>
                : <a className="ac-btn ac-ref-btn" href={whatsAppHref(link)} target="_blank" rel="noopener noreferrer">{R.whatsapp}</a>}
            </div>
          </div>
          {line.error
            ? <p className="ac-msg ac-msg--error ac-ref-msg" role="alert">{line.text}</p>
            : <p className="ac-msg ac-ref-msg" role="status">{line.text}</p>}
        </>
      ) : (
        <p className="ac-ref-note">{R.onItsWay}</p>
      )}
      {fee && <p className="ac-ref-fee">{fee}</p>}
      <button type="button" className="ac-ref-toggle" aria-expanded={open} aria-controls={`${uid}-details`} onClick={() => setOpen(o => !o)}>
        {open ? R.hide : R.details}
      </button>
      {open && <Details offer={offer} id={`${uid}-details`} />}
    </article>
  )
}

function Line({ l }) {
  const amount = (l.status === 'qualified' || l.status === 'paid') && l.payout_amount != null ? gbp(l.payout_amount) : ''
  return (
    <li className="ac-ref-item">
      <span className="ac-ref-name">{l.referred_name || R.someone}</span>
      <span className={`ac-ref-pill ac-ref-pill--${l.status}`}>{R.statuses[l.status] || l.status}</span>
      <span className="ac-ref-meta">{[l.product_name, dayMonthYear(l.introduced_at)].filter(Boolean).join(', ')}</span>
      {amount && <span className="ac-ref-amount">{amount}</span>}
    </li>
  )
}

export default function Referrals({ readOnly = false }) {
  const [state, setState] = useState({ status: 'loading' })
  const load = useCallback(() => {
    setState(s => (s.status === 'ready' ? s : { status: 'loading' }))
    return loadReferrals().then(
      d => setState({ status: 'ready', ...d }),
      () => setState(s => (s.status === 'ready' ? s : { status: 'error' })),
    )
  }, [])
  useEffect(() => { load() }, [load])

  const head = <h2 id="referrals-h" className="ac-h2">{R.title}</h2>

  if (state.status === 'loading') {
    return (
      <section id="referrals" className="ac-card" aria-labelledby="referrals-h" aria-busy="true">
        {head}
        <div className="ac-ref-loading" />
      </section>
    )
  }
  if (state.status === 'error') {
    return (
      <section id="referrals" className="ac-card" aria-labelledby="referrals-h">
        {head}
        <p className="ac-lede" role="alert">{R.loadFailed}</p>
        <div className="ac-row"><button type="button" className="ac-btn" onClick={load}>{R.tryAgain}</button></div>
      </section>
    )
  }
  if (isQuiet(state)) {
    return (
      <section id="referrals" className="ac-card" aria-labelledby="referrals-h">
        {head}
        <p className="ac-lede">{R.nothing}</p>
      </section>
    )
  }

  const t = totals(state.lines)
  return (
    <section id="referrals" className="ac-card ac-card--referrals" aria-labelledby="referrals-h">
      {head}
      <p className="ac-lede">{state.offers.length ? R.lede : R.ledeNoLink}</p>
      <dl className="ac-ref-totals">
        <div className="ac-ref-total"><dt>{R.pending}</dt><dd>{t.pending}</dd></div>
        <div className="ac-ref-total"><dt>{R.owed}</dt><dd>{gbp(t.owed)}</dd></div>
        <div className="ac-ref-total"><dt>{R.paid}</dt><dd>{gbp(t.paid)}</dd></div>
      </dl>
      {state.offers.map(o => <OfferCard key={o.product_id} offer={o} readOnly={readOnly} />)}
      <div className="ac-ref-listbox">
        <h3 className="ac-ref-h3" id="ref-list-h">{R.listTitle}</h3>
        {state.lines.length === 0
          ? <p className="ac-ref-text">{R.empty}</p>
          : <ul className="ac-ref-list" aria-labelledby="ref-list-h">{state.lines.map(l => <Line key={l.id} l={l} />)}</ul>}
      </div>
    </section>
  )
}
